<template>
  <v-container>
    <v-form ref="form" :lazy-validation="true">
      <validation-observer ref="validationObserver" v-slot="{ invalid }">
        <v-row class="mb-2">
          <v-col>
            <validation-provider
              :name="$t('form.fields.title')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="formData.title"
                :label="$t('form.fields.title') + ' *'"
                required
                outlined
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-2" v-if="selectionOptions.length > 0">
          <v-col>
            <validation-provider
              :name="$t('form.fields.for')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-select
                :label="$t('form.fields.for') + ' *'"
                v-model="formData.selectedOption"
                outlined
                :items="selectionOptions"
                required
                :error-messages="errors"
              ></v-select>
            </validation-provider>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-2" v-if="!isExhibitionPage">
          <v-col>
            <validation-provider
              :name="$t('form.fields.for')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-select
                :label="$t('form.fields.for') + ' *'"
                v-model="formData.categoryId"
                outlined
                :items="categories"
                required
                :error-messages="errors"
              ></v-select>
            </validation-provider>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-2" v-if="isResearch">
          <v-col>
            <validation-provider
              :name="$t('form.fields.sum')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="formData.sum"
                :label="$t('form.fields.sum') + ' *'"
                required
                outlined
                type="number"
                prepend-icon="mdi-currency-eur"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-2" v-if="!pageSettings.hideAbstractAndKeywords">
          <v-col>
            <validation-provider
              name="Abstract"
              v-slot="{ errors }"
              rules="required"
            >
              <v-textarea
                v-model="formData.abstract"
                label="Abstract *"
                required
                outlined
                :error-messages="errors"
              ></v-textarea>
            </validation-provider>
          </v-col>
        </v-row>

        <v-row
          class="mt-n8 mb-2"
          v-if="isExhibitionPage && !pageSettings.hideAbstractAndKeywords"
        >
          <v-col>
            <v-text-field
              v-model="formData.keywords"
              :label="$t('form.fields.keywords')"
              outlined
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-2">
          <v-col>
            <v-file-input
              v-model="documents"
              accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.zip,.xls,.xlsx"
              :label="$t('form.fields.documents')"
              :hint="isExhibitionPage ? '' : $t('form.fields.documentsHint')"
              :loading="loading"
              persistent-hint
              multiple
              small-chips
              outlined
              prepend-icon="mdi-paperclip"
              :error-messages="errors"
            ></v-file-input>
          </v-col>
        </v-row>

        <v-row class="mt-n4 mb-2" v-if="isExhibitionPage">
          <v-col cols="11">
            <span class="headline"> {{ $t("form.author") }} 1 </span>
          </v-col>
        </v-row>

        <v-row class="mt-n4 mb-2">
          <v-col cols="10">
            <validation-provider
              :name="$t('form.fields.name')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="formData.name"
                :label="$t('form.fields.name') + ' *'"
                required
                outlined
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col>
            <v-text-field
              v-model="formData.authorTitle"
              :label="$t('form.fields.title')"
              outlined
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-2" v-if="isExhibitionPage">
          <v-col>
            <v-text-field
              v-model="formData.affiliation"
              :label="$t('form.fields.affiliation')"
              outlined
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-2">
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.email')"
              v-slot="{ errors }"
              vid="originalEmail"
              rules="required|email|confirmed:@confirmationEmail"
            >
              <v-text-field
                ref="originalMail"
                v-model="formData.email"
                :label="$t('form.fields.email') + ' *'"
                required
                outlined
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.confirmationEmail')"
              v-slot="{ errors }"
              vid="confirmationEmail"
              rules="required|email|confirmed:@originalEmail"
            >
              <v-text-field
                v-model="confirmationMail"
                :label="$t('form.fields.confirmationEmail') + ' *'"
                required
                outlined
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="mt-n8 mb-2">
            <v-text-field
              :label="$t('form.fields.phoneNumber')"
              v-model="formData.phoneNumber"
              outlined
              :error-messages="errors"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-2">
          <v-col>
            <validation-provider
              :name="$t('form.fields.street')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                :label="$t('form.fields.street') + ' *'"
                v-model="formData.street"
                required
                outlined
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="2">
            <validation-provider
              :name="$t('form.fields.zip')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                :label="$t('form.fields.zip') + ' *'"
                v-model="formData.zip"
                required
                outlined
                :error-messages="errors"
              ></v-text-field
            ></validation-provider>
          </v-col>
          <v-col cols="3">
            <validation-provider
              :name="$t('form.fields.city')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                :label="$t('form.fields.city') + ' *'"
                v-model="formData.city"
                required
                outlined
                :error-messages="errors"
              ></v-text-field
            ></validation-provider>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="mt-n8 mb-2">
            <validation-provider
              :name="$t('form.fields.country')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                :label="$t('form.fields.country') + ' *'"
                v-model="formData.country"
                required
                outlined
                :error-messages="errors"
              ></v-text-field
            ></validation-provider>
          </v-col>
        </v-row>
        <div v-for="(author, index) in formData.authors" :key="author">
          <v-row class="mt-n4 mb-2">
            <v-col cols="11">
              <span class="headline">
                {{ $t("form.author") }} {{ index + 2 }}
              </span>
            </v-col>
            <v-col cols="1" align="right">
              <v-btn icon @click="removeAuthor(index)">
                <v-icon color="primary">mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <v-row class="mt-n4 mb-2">
            <v-col cols="10">
              <validation-provider
                :name="$t('form.fields.name')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="author.name"
                  :label="$t('form.fields.name') + ' *'"
                  required
                  outlined
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col>
              <v-text-field
                v-model="author.title"
                :label="$t('form.fields.title')"
                outlined
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row class="mt-n8 mb-2" v-if="isExhibitionPage">
            <v-col>
              <v-text-field
                v-model="author.affiliation"
                :label="$t('form.fields.affiliation')"
                outlined
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row class="mt-n8 mb-2">
            <v-col cols="6">
              <validation-provider
                :name="$t('form.fields.email')"
                v-slot="{ errors }"
                :vid="`originalEmail-${index}`"
                :rules="`required|email|confirmed:@confirmationEmail-${index}`"
              >
                <v-text-field
                  ref="originalMail"
                  v-model="author.email"
                  :label="$t('form.fields.email') + ' *'"
                  required
                  outlined
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="6">
              <validation-provider
                :name="$t('form.fields.confirmationEmail')"
                v-slot="{ errors }"
                :vid="`confirmationEmail-${index}`"
                :rules="`required|email|confirmed:@originalEmail-${index}`"
              >
                <v-text-field
                  v-model="author.confirmationMail"
                  :label="$t('form.fields.confirmationEmail') + ' *'"
                  required
                  outlined
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
          </v-row>

          <v-row class="mt-n8 mb-2">
            <v-col>
              <validation-provider
                :name="$t('form.fields.street')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  :label="$t('form.fields.street') + ' *'"
                  v-model="author.street"
                  required
                  outlined
                  :error-messages="errors"
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="2">
              <validation-provider
                :name="$t('form.fields.zip')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  :label="$t('form.fields.zip') + ' *'"
                  v-model="author.zip"
                  required
                  outlined
                  :error-messages="errors"
                ></v-text-field
              ></validation-provider>
            </v-col>
            <v-col cols="3">
              <validation-provider
                :name="$t('form.fields.city')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  :label="$t('form.fields.city') + ' *'"
                  v-model="author.city"
                  required
                  outlined
                  :error-messages="errors"
                ></v-text-field
              ></validation-provider>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="mt-n8 mb-2">
              <validation-provider
                :name="$t('form.fields.country')"
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  :label="$t('form.fields.country') + ' *'"
                  v-model="author.country"
                  required
                  outlined
                  :error-messages="errors"
                ></v-text-field
              ></validation-provider>
            </v-col>
          </v-row>
        </div>

        <v-row justify="center" class="mt-n6 mb-2" v-if="isExhibitionPage">
          <v-btn color="primary" raised @click="addAuthor">
            <v-icon>mdi-plus</v-icon>
            &nbsp;{{ $t("form.btn.addAuthor") }}
          </v-btn>
        </v-row>

        <v-row class="mt-n4">
          <v-col>
            <v-checkbox v-model="acceptTerms">
              <template #label>
                <span
                  class="black--text mt-1"
                  v-html="$t('form.fields.privacy', { url: privacyUrl })"
                ></span>
              </template>
            </v-checkbox>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-n12" v-if="!didVerify">
          <v-col>
            <div id="mcaptcha__widget-container" />
          </v-col>
        </v-row>

        <v-row class="mt-n4 mb-2" justify="end">
          <v-col cols="3" lg="2">
            <v-btn
              color="primary"
              :disabled="invalid || !acceptTerms"
              :loading="loading"
              @click="submit"
            >
              {{ $t("form.btn.send") }}
            </v-btn>
          </v-col>
        </v-row>
      </validation-observer>
    </v-form>
    <v-dialog v-model="dialog" width="500">
      <v-card>
        <v-card-title class="white--text headline primary" primary-title>
          {{ $t("form.confirmationDialog.title") }}
        </v-card-title>

        <v-card-text class="pa-md-4">
          {{ $t("form.confirmationDialog.confirmationText") }}
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="reload">
            {{ $t("form.confirmationDialog.buttonText") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  submissionResource,
  verificationResource,
  attachmentResource,
  submissionMailResource,
  pageTypeResource,
  pageSettingsResource,
  mcaptchaSettingsResource,
} from "@/api/resources";
import { i18n } from "../plugins/i18n";

export default {
  data: () => ({
    pageSettings: {},
    formData: { authors: [] },
    documents: [],
    categories: [],
    confirmationMail: "",
    acceptTerms: false,
    dialog: false,
    loading: false,
    isExhibitionPage: false,
    didVerify: false,
  }),
  async mounted() {
    const captchaSettings = (await mcaptchaSettingsResource.get()).data;
    let config = {
      widgetLink: new URL(captchaSettings.widgetLink),
    };
    // eslint-disable-next-line no-undef
    new mcaptchaGlue.default(config);
    this.pageSettings = (
      await pageSettingsResource.get(
        window.pageId || "ckea15wha002vn3s8833n08be",
        {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        }
      )
    ).data;
    const pageType = (
      await pageTypeResource.get("", {
        headers: { "x-xsrf-token": this.$cookies.get("oegg-cms.csrf") },
      })
    ).data.type;
    this.isExhibitionPage = pageType !== "OEGG";
    if (!this.isExhibitionPage) {
      this.categories = [
        { value: "research", text: i18n.t("form.researchFunding") },
      ];
      this.formData.categoryId = "research";
    }
  },
  methods: {
    addAuthor() {
      this.formData.authors.push({});
    },
    removeAuthor(index) {
      this.formData.authors.splice(index, 1);
    },
    async submit() {
      if (!this.didVerify) {
        const verificationToken = document.getElementById("mcaptcha__token")
          .value;
        if (!verificationToken) {
          return;
        }
        await verificationResource.post("", {
          token: verificationToken,
        });
      }
      this.didVerify = true;
      this.loading = true;
      if (!this.isResearch && !this.isExhibitionPage) {
        this.formData.documents = [];
        for (let index in this.documents) {
          const fileData = this.documents[index];
          const formData = new FormData();
          formData.append("file", fileData);
          const data = await attachmentResource.post("", formData, {
            headers: { "x-xsrf-token": this.$cookies.get("oegg-cms.csrf") },
          });
          this.formData.documents.push({
            title: fileData.name,
            document: data.data,
          });
        }

        this.formData.date = Date.now();
        await submissionResource.post("", this.formData, {
          headers: { "x-xsrf-token": this.$cookies.get("oegg-cms.csrf") },
        });
      }

      const formData = new FormData();
      if (this.isResearch || this.isExhibitionPage) {
        this.documents.forEach((document) => {
          formData.append("file", document);
        });
      }
      for (let key in this.formData) {
        formData.append(
          key,
          key === "authors"
            ? JSON.stringify(this.formData[key])
            : this.formData[key]
        );
      }
      await submissionMailResource.post("", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
        },
      });
      this.loading = false;
      this.dialog = true;
      this.reset();
    },

    reset() {
      this.formData = { authors: [] };
      this.documents = [];
      this.confirmationMail = "";
      this.acceptTerms = false;
      this.$nextTick(() => this.$refs.validationObserver.reset());
    },

    reload() {
      location.reload();
    },
  },
  computed: {
    privacyUrl() {
      return window.privacyUrl;
    },
    isResearch() {
      if (this.isExhibitionPage) {
        return false;
      }
      return this.formData.categoryId === "research";
    },
    selectionOptions() {
      return this.isExhibitionPage && this.pageSettings.selectionOptions
        ? this.pageSettings.selectionOptions.map((option) => {
            return {
              value: option.title,
              text: option.title,
            };
          })
        : [];
    },
  },
};
</script>
